import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  InputLabel,
  makeStyles,
  Switch,
  TextField
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CloseIcon from '@material-ui/icons/Close';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { getListAgencyBusiness } from 'src/features/agencySlice';
import { transmitActiveStatus, updateDevice } from 'src/features/deviceSlice';
import { getBusinessInfo } from 'src/features/userSlice';

const useStyles = makeStyles(() => ({
  configDialogPaper: {
    width: '100%',
    maxWidth: '960px',
    borderRadius: '2px',
    overflow: 'hidden'
  },
  configDialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 22px',
    borderBottom: '1px solid #e6ebf0'
  },
  configDialogTitle: {
    margin: 0,
    color: '#13293d',
    fontSize: '18px',
    fontWeight: 700
  },
  configDialogClose: {
    color: '#ea1d48',
    padding: 0
  },
  configDialogContent: {
    padding: '26px 22px 24px'
  },
  topSwitchRow: {
    marginBottom: '18px'
  },
  configSwitchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minHeight: '32px'
  },
  switchLabel: {
    margin: 0,
    color: '#2d2d2d',
    fontSize: '15px',
    fontWeight: 500
  },
  fieldLabel: {
    marginBottom: '12px',
    color: '#577286',
    fontSize: '16px',
    fontWeight: 400
  },
  configTextField: {
    '& .MuiOutlinedInput-root': {
      minHeight: '40px',
      background: '#fff'
    },
    '& .MuiOutlinedInput-input': {
      fontSize: '15px',
      padding: '12px 14px'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#d9e0e7'
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0
    }
  },
  transmissionSwitch: {
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#ea1d48'
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#ea1d48'
    }
  },
  commitmentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '28px',
    color: '#2d2d2d',
    fontSize: '15px',
    lineHeight: 1.5
  },
  commitmentCheckbox: {
    padding: 0,
    marginTop: '2px'
  },
  configActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '32px',
    marginTop: '26px'
  },
  primaryActionButton: {
    minWidth: '168px',
    height: '40px',
    background: '#d9d9d9',
    color: '#fff',
    fontSize: '15px',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
      background: '#d9d9d9'
    },
    '&.Mui-disabled': {
      color: '#ffffff',
      background: '#d9d9d9'
    }
  },
  primaryActionButtonActive: {
    background: '#ea1d48',
    '&:hover': {
      background: '#ea1d48'
    }
  },
  secondaryActionButton: {
    minWidth: '168px',
    height: '40px',
    background: '#fff',
    border: '1px solid #ea1d48',
    color: '#ea1d48',
    fontSize: '15px',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
      background: '#fff'
    }
  }
}));

const getInitialConfig = row => ({
  business_name: row?.transport_operator || '',
  tax_code: row?.tax_code || '',
  business_id: row?.business?.id || '',
  chassis_number: row?.chassis_number || '',
  passenger_capacity: row?.passenger_capacity || '',
  max_payload: row?.max_payload || ''
});

const validationSchema = Yup.object().shape({
  business_name: Yup.string()
    .trim()
    .required('Tên đơn vị kinh doanh không được để trống'),
  tax_code: Yup.string()
    .trim()
    .required('Mã số thuế không được để trống'),
  business_id: Yup.mixed().required('Loại hình kinh doanh không được để trống')
});

const TransmissionConfigDialog = ({ onClose, onSuccess, open, row }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const listAgencyBusiness = useSelector(state => state.agencySlice.listAgencyBusiness);
  const [isCommitmentChecked, setIsCommitmentChecked] = useState(false);
  const [isInfoTransmit, setIsInfoTransmit] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);

  useEffect(() => {
    if (!open || !row) {
      setIsCommitmentChecked(false);
      setIsInfoTransmit(false);
      return;
    }

    dispatch(getListAgencyBusiness());

    const selectedPhone = row?.end_user?.user?.phone || row?.endUser?.user?.phone || null;
    if (selectedPhone) {
      dispatch(getBusinessInfo({ phone: selectedPhone }));
    }

    setIsCommitmentChecked(false);
    setIsInfoTransmit(false);
  }, [dispatch, open, row]);

  const handleClose = () => {
    if (isSavingConfig) {
      return;
    }

    onClose();
  };

  const handleInfoTransmitChange = setFieldValue => event => {
    const checked = event.target.checked;
    setIsInfoTransmit(checked);

    if (checked) {
      setFieldValue('business_name', row?.transport_operator || '');
      setFieldValue('tax_code', row?.tax_code || '');
      setFieldValue('business_id', row?.business?.id || '');
      return;
    }

    setFieldValue('business_name', '');
    setFieldValue('tax_code', '');
    setFieldValue('business_id', '');
  };

  const handleSaveTransmissionConfig = async values => {
    if (!row) {
      return;
    }

    setIsSavingConfig(true);

    try {
      await dispatch(
        transmitActiveStatus({
          id: row.id,
          data: { status: 1 }
        })
      ).unwrap();

      await dispatch(
        updateDevice({
          id: row.id,
          data: {
            vehicle_license_plate: '',
            title_device_type:
              row?.vehicle?.vehicle_type?.title ||
              row?.deviceType?.name ||
              '',
            slots: row?.vehicle?.vehicle_type?.slots || null,
            tonnage: row?.vehicle?.vehicle_type?.tonnage || null,
            name_of_user_for_gps: null,
            version: row?.hardware_version || '',
            sim_phone: row?.sim || '',
            business_id: values.business_id || null,
            passenger_capacity: values.passenger_capacity
              ? Number(values.passenger_capacity)
              : null,
            tax_code: String(values.tax_code || '').trim() || null,
            max_payload: values.max_payload
              ? Number(values.max_payload)
              : null,
            transport_operator:
              String(values.business_name || '').trim() || null,
            chassis_number:
              String(values.chassis_number || '').trim() || null
          }
        })
      ).unwrap();

      onSuccess(row.id);
    } finally {
      setIsSavingConfig(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: classes.configDialogPaper }}
    >
      <div className={classes.configDialogHeader}>
        <h3 className={classes.configDialogTitle}>Cấu hình tích truyền</h3>
        <IconButton onClick={handleClose} className={classes.configDialogClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Formik
        initialValues={getInitialConfig(row)}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSaveTransmissionConfig}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
          setFieldValue
        }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent className={classes.configDialogContent}>
              <Grid container spacing={2}>
                <Grid item md={4} xs={12} className={classes.topSwitchRow}>
                  <div className={classes.configSwitchRow}>
                    <InputLabel className={classes.switchLabel}>
                      Tích truyền QCVN 06:2024/BCA
                    </InputLabel>
                    <Switch checked className={classes.transmissionSwitch} />
                  </div>
                </Grid>
                <Grid item md={8} xs={12} className={classes.topSwitchRow}>
                  <div className={classes.configSwitchRow}>
                    <InputLabel className={classes.switchLabel}>
                      Lấy thông tin từ tài khoản
                    </InputLabel>
                    <Switch
                      checked={isInfoTransmit}
                      onChange={handleInfoTransmitChange(setFieldValue)}
                      className={classes.transmissionSwitch}
                    />
                  </div>
                </Grid>
                <Grid item md={4} xs={12}>
                  <InputLabel className={classes.fieldLabel}>
                    Tên đơn vị kinh doanh vận tải <span className="text-danger">*</span>
                  </InputLabel>
                  <TextField
                    disabled={isInfoTransmit}
                    error={Boolean(touched.business_name && errors.business_name)}
                    helperText={touched.business_name && errors.business_name}
                    size="small"
                    fullWidth
                    variant="outlined"
                    name="business_name"
                    value={values.business_name}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    className={classes.configTextField}
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <InputLabel className={classes.fieldLabel}>
                    Mã số thuế <span className="text-danger">*</span>
                  </InputLabel>
                  <TextField
                    disabled={isInfoTransmit}
                    error={Boolean(touched.tax_code && errors.tax_code)}
                    helperText={touched.tax_code && errors.tax_code}
                    size="small"
                    fullWidth
                    variant="outlined"
                    name="tax_code"
                    value={values.tax_code}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    className={classes.configTextField}
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <InputLabel className={classes.fieldLabel}>
                    Loại hình kinh doanh vận tải <span className="text-danger">*</span>
                  </InputLabel>
                  <Autocomplete
                    disabled={isInfoTransmit}
                    size="small"
                    options={listAgencyBusiness || []}
                    getOptionLabel={option => option.title || ''}
                    value={(listAgencyBusiness || []).find(
                      option => Number(option.id) === Number(values.business_id)
                    ) || null}
                    onChange={(_, value) => {
                      setFieldValue('business_id', value?.id || '');
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Chọn loại hình kinh doanh"
                        error={Boolean(touched.business_id && errors.business_id)}
                        helperText={touched.business_id && errors.business_id}
                        className={classes.configTextField}
                      />
                    )}
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <InputLabel className={classes.fieldLabel}>Số khung</InputLabel>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    name="chassis_number"
                    value={values.chassis_number}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    className={classes.configTextField}
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <InputLabel className={classes.fieldLabel}>Số người chuyên chở</InputLabel>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    name="passenger_capacity"
                    value={values.passenger_capacity}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    className={classes.configTextField}
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <InputLabel className={classes.fieldLabel}>Tải trọng xe (kg)</InputLabel>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    name="max_payload"
                    value={values.max_payload}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    className={classes.configTextField}
                  />
                </Grid>
              </Grid>
              <div className={classes.commitmentRow}>
                <Checkbox
                  checked={isCommitmentChecked}
                  onChange={event => setIsCommitmentChecked(event.target.checked)}
                  className={classes.commitmentCheckbox}
                />
                <span>
                  Tôi cam kết các thông tin khai báo là chính xác. Nếu có sai lệch tôi hoàn toàn chịu trách nhiệm với Bộ công an.
                </span>
              </div>
              <div className={classes.configActions}>
                <Button
                  variant="contained"
                  className={classes.secondaryActionButton}
                  onClick={handleClose}
                  disabled={isSavingConfig}
                >
                  Thoát
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  className={`${classes.primaryActionButton} ${isCommitmentChecked ? classes.primaryActionButtonActive : ''}`}
                  disabled={isSavingConfig || !isCommitmentChecked}
                >
                  Lưu lại
                </Button>
              </div>
            </DialogContent>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

TransmissionConfigDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  row: PropTypes.object
};

export default TransmissionConfigDialog;
